import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from '../UI/Button';

const HeaderContainer = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1rem 0;
  background: rgba(10, 26, 46, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  transition: all 0.3s ease;
  
  &.scrolled {
    padding: 0.5rem 0;
    background: rgba(10, 26, 46, 0.98);
  }
  
  /* Large monitors (1440px+) */
  @media (min-width: 1440px) {
    padding: 1.5rem 0;
    
    &.scrolled {
      padding: 1rem 0;
    }
  }
  
  /* Ultra-wide monitors (1920px+) */
  @media (min-width: 1920px) {
    padding: 2rem 0;
    
    &.scrolled {
      padding: 1.25rem 0;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0.75rem 0;
    
    &.scrolled {
      padding: 0.5rem 0;
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  
  /* Large monitors get wider container */
  @media (min-width: 1440px) {
    max-width: 1400px;
    padding: 0 3rem;
  }
  
  @media (min-width: 1920px) {
    max-width: 1600px;
    padding: 0 4rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0 1rem;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  z-index: 1002;
  
  /* Responsive gap for larger screens */
  @media (min-width: 1440px) {
    gap: 1.5rem;
  }
  
  @media (min-width: 1920px) {
    gap: 2rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 0.5rem;
  }
`;

const LogoImage = styled.img`
  /* Responsive logo sizing using clamp() for smooth scaling */
  height: clamp(40px, 4vw, 80px);
  width: auto;
  transition: all 0.3s ease;
  filter: drop-shadow(0 0 10px rgba(255, 229, 0, 0.3));
  
  /* Minimum sizes for different breakpoints */
  // @media (min-width: 768px) {
  //   height: clamp(50px, 3.5vw, 70px);
  // }
  
  @media (min-width: 1440px) {
    height: clamp(60px, 4vw, 80px);
  }
  
  @media (min-width: 1920px) {
    height: clamp(70px, 3.5vw, 85px);
  }
  
  &:hover {
    transform: scale(1.05);
    filter: drop-shadow(0 0 15px rgba(255, 229, 0, 0.5));
  }
  
  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      transform: none;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: clamp(35px, 8vw, 45px);
  }
`;

const LogoText = styled.div`
  display: none;
  font-size: clamp(1rem, 2vw, 1.8rem);
  font-weight: 800;
  color: #FFE500;
  font-family: 'JetBrains Mono', monospace;
  text-shadow: 0 0 20px rgba(255, 229, 0, 0.5);
  
  @media (min-width: 1440px) {
    font-size: clamp(1.2rem, 1.8vw, 2rem);
  }
  
  @media (min-width: 1920px) {
    font-size: clamp(1.4rem, 1.6vw, 2.2rem);
  }
`;

const NavLinks = styled.ul`
  display: flex;
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;
  
  /* Responsive gap for larger screens */
  @media (min-width: 1440px) {
    gap: 2.5rem;
  }
  
  @media (min-width: 1920px) {
    gap: 3rem;
  }
  
  li {
    flex-shrink: 0;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      &:last-child a {
        border-bottom: none;
      }
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.colors.background.secondary};
    flex-direction: column;
    padding: 2rem;
    gap: 1rem;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    transform: translateX(${({ $isOpen }) => $isOpen ? '0' : '100%'});
    transition: transform 0.3s ease;
    backdrop-filter: blur(20px);
    z-index: 999;
    overflow-y: auto;
    visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
    height: calc(100vh - 80px);
    justify-content: flex-start;
    align-items: stretch;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    top: 70px;
    height: calc(100vh - 70px);
    padding: 1.5rem;
    gap: 0.75rem;
  }
`;

const NavLink = styled(motion.a)`
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  font-weight: 500;
  text-transform: uppercase;
  font-size: clamp(0.75rem, 1vw, 0.9rem);
  letter-spacing: 0.5px;
  position: relative;
  padding: 0.5rem 0;
  cursor: pointer;
  transition: color 0.3s ease;
  display: block;
  
  /* Larger font sizes for big monitors */
  @media (min-width: 1440px) {
    font-size: clamp(0.8rem, 0.9vw, 1rem);
  }
  
  @media (min-width: 1920px) {
    font-size: clamp(0.85rem, 0.8vw, 1.1rem);
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary.yellow};
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: ${({ theme }) => theme.colors.primary.yellow};
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.1rem;
    padding: 1rem;
    text-align: center;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    margin: 0;
    border-radius: 8px;
    
    &::after {
      display: none;
    }
    
    &:hover {
      background: rgba(255, 229, 0, 0.1);
    }
    
    &:active {
      background: rgba(255, 229, 0, 0.2);
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    padding: 0.875rem;
  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  flex-direction: column;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  z-index: 1001;
  outline: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 6px;
    gap: 3px;
  }
`;

const HamburgerLine = styled.span`
  width: 25px;
  height: 2px;
  background: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.3s ease;
  transform-origin: center;
  
  ${({ $isOpen }) => $isOpen && `
    &:nth-child(1) {
      transform: rotate(45deg) translate(6px, 6px);
    }
    &:nth-child(2) {
      opacity: 0;
    }
    &:nth-child(3) {
      transform: rotate(-45deg) translate(6px, -6px);
    }
  `}
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 22px;
    height: 2px;
    
    ${({ $isOpen }) => $isOpen && `
      &:nth-child(1) {
        transform: rotate(45deg) translate(5px, 5px);
      }
      &:nth-child(3) {
        transform: rotate(-45deg) translate(5px, -5px);
      }
    `}
  }
`;

const RegisterButton = styled.div`
  z-index: 1002;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MobileRegisterButton = styled.div`
  display: none;
  flex-shrink: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
    padding: 1rem;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    margin-top: auto;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1rem;
  }
`;

const MobileOverlay = styled.div`
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 998;
  }
`;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navItems = [
    { label: 'About Us', href: '#about' },
    { label: 'Event', href: '#event' },
    { label: 'Speakers', href: '#speakers' },
    { label: 'Schedule', href: '#schedule' },
    { label: 'Hacking Village', href: '#hackingvillage' },
    { label: 'CTF', href: '#ctf' },
    { label: 'Become A Sponsor', href: '#becomesponsor' }
  ];

  const toggleMobileMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMobileMenuOpen(prev => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (e, href) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
    
    const scrollToTarget = () => {
      const element = document.querySelector(href);
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    };
    
    if (isMobileMenuOpen) {
      setTimeout(scrollToTarget, 350);
    } else {
      scrollToTarget();
    }
  };

  const handleLogoError = (e) => {
    e.target.style.display = 'none';
    const logoText = e.target.parentNode.querySelector('div');
    if (logoText) {
      logoText.style.display = 'block';
    }
  };

  return (
    <>
      <MobileOverlay $isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />
      <HeaderContainer
        className={isScrolled ? 'scrolled' : ''}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Nav>
          <LogoContainer>
            <LogoImage 
              src="/bsidesabq.webp" 
              alt="BSides Albuquerque Logo"
              onError={handleLogoError}
            />
            <LogoText>
              BSides ABQ
            </LogoText>
          </LogoContainer>
          
          <NavLinks $isOpen={isMobileMenuOpen}>
            {navItems.map((item, index) => (
              <li key={item.label}>
                <NavLink
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
            
            <MobileRegisterButton>
              <Button 
                variant="primary" 
                size="small" 
                href="https://www.eventbrite.com/e/security-bsides-albuquerque-cybersecurity-conference-tickets-1244346185939"
                onClick={closeMobileMenu}
              >
                🎫 Register
              </Button>
            </MobileRegisterButton>
          </NavLinks>
          
          <MobileMenuToggle
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            <HamburgerLine $isOpen={isMobileMenuOpen} />
            <HamburgerLine $isOpen={isMobileMenuOpen} />
            <HamburgerLine $isOpen={isMobileMenuOpen} />
          </MobileMenuToggle>
          
          <RegisterButton>
            <Button 
              variant="primary" 
              size="small" 
              href="https://www.eventbrite.com/e/security-bsides-albuquerque-cybersecurity-conference-tickets-1244346185939"
            >
              🎫 Register
            </Button>
          </RegisterButton>
        </Nav>
      </HeaderContainer>
    </>
  );
};

export default Header;